import {
    AccountingReportComparison,
    AccountingReportComparisonDiff,
    AccountingReportCycle,
    AccountingReportCycleOptionType,
    AccountingReportRatioType,
} from "@common/constants";
import { resolveEffectiveReportCycle } from "@common/helpers/reports";
import {
    AccountingReportType,
    ACCOUNTING_REPORT_COMPARISON_OPTIONS,
} from "@common/types/reports/report-type";
import { TaskType } from "@common/types/task";
import type { FiltersTypes } from "@common/types/filters/general";

export type ReportTaskType = TaskType.PROFIT_LOSS | TaskType.BALANCE_SHEET;

type ReportDisplayFilterValues = Pick<
    FiltersTypes,
    "cycle" | "comparisons" | "comparisonDiffs" | "ratioTypes"
>;

export const REPORT_TYPE_BY_TASK_TYPE: Record<
    ReportTaskType,
    AccountingReportType
> = {
    [TaskType.PROFIT_LOSS]: AccountingReportType.PROFIT_LOSS,
    [TaskType.BALANCE_SHEET]: AccountingReportType.BALANCE_SHEET,
};

export interface ReportComparisonOptions {
    comparisons: AccountingReportComparison[];
    comparisonDiffs: AccountingReportComparisonDiff[];
    ratioTypes: AccountingReportRatioType[];
}

export interface ReportDisplayFilters extends ReportComparisonOptions {
    cycle: AccountingReportCycleOptionType;
}

export type ReportDisplayFiltersPatch = Partial<ReportDisplayFilters>;

export const DEFAULT_REPORT_CYCLE = AccountingReportCycle.MONTH;

export const isReportTaskType = (type: TaskType): type is ReportTaskType =>
    type === TaskType.PROFIT_LOSS || type === TaskType.BALANCE_SHEET;

export const readReportComparisonOptions = (
    filters: FiltersTypes | undefined,
): ReportComparisonOptions => ({
    comparisons: filters?.comparisons ?? [],
    comparisonDiffs: filters?.comparisonDiffs ?? [],
    ratioTypes: filters?.ratioTypes ?? [],
});

export const readReportDisplayFilters = (
    filters: FiltersTypes | undefined,
): ReportDisplayFilters => ({
    cycle: resolveEffectiveReportCycle({
        cycle: filters?.cycle ?? DEFAULT_REPORT_CYCLE,
    }),
    ...readReportComparisonOptions(filters),
});

const pickReportDisplayFilters = (
    filters: FiltersTypes | undefined,
): ReportDisplayFilterValues => ({
    ...(filters?.cycle ? { cycle: filters.cycle } : {}),
    ...(filters?.comparisons?.length
        ? { comparisons: filters.comparisons }
        : {}),
    ...(filters?.comparisonDiffs?.length
        ? { comparisonDiffs: filters.comparisonDiffs }
        : {}),
    ...(filters?.ratioTypes?.length ? { ratioTypes: filters.ratioTypes } : {}),
});

const omitReportDisplayFilters = (
    filters: FiltersTypes | undefined,
): FiltersTypes => {
    const {
        cycle: _cycle,
        comparisons: _comparisons,
        comparisonDiffs: _comparisonDiffs,
        ratioTypes: _ratioTypes,
        ...rest
    } = filters ?? {};
    return rest;
};

const keepAllowedValues = <T>(
    values: T[] | undefined,
    allowedValues: readonly T[],
): T[] | undefined => values?.filter((value) => allowedValues.includes(value));

const pickReportDisplayFiltersForTaskType = ({
    taskType,
    filters,
}: {
    taskType: ReportTaskType;
    filters: FiltersTypes | undefined;
}): ReportDisplayFilterValues => {
    const allowedOptions =
        ACCOUNTING_REPORT_COMPARISON_OPTIONS[
            REPORT_TYPE_BY_TASK_TYPE[taskType]
        ];
    const comparisons = keepAllowedValues(
        filters?.comparisons,
        allowedOptions.comparisons,
    );
    return pickReportDisplayFilters({
        cycle: filters?.cycle,
        comparisons,
        comparisonDiffs: comparisons?.length
            ? keepAllowedValues(
                  filters?.comparisonDiffs,
                  allowedOptions.comparisonDiffs,
              )
            : [],
        ratioTypes: keepAllowedValues(
            filters?.ratioTypes,
            allowedOptions.ratioTypes,
        ),
    });
};

export const replaceReportDisplayFilters = ({
    taskType,
    filters,
    displayFilters,
}: {
    taskType: ReportTaskType;
    filters: FiltersTypes | undefined;
    displayFilters: FiltersTypes | undefined;
}): FiltersTypes => ({
    ...omitReportDisplayFilters(filters),
    ...pickReportDisplayFiltersForTaskType({
        taskType,
        filters: displayFilters,
    }),
});

export const applyReportDisplayPatch = ({
    filters,
    patch,
}: {
    filters: FiltersTypes | undefined;
    patch: ReportDisplayFiltersPatch;
}): FiltersTypes => {
    const next = { ...readReportDisplayFilters(filters), ...patch };
    return {
        ...omitReportDisplayFilters(filters),
        ...pickReportDisplayFilters({
            cycle: next.cycle === DEFAULT_REPORT_CYCLE ? undefined : next.cycle,
            comparisons: next.comparisons,
            comparisonDiffs: next.comparisons.length
                ? next.comparisonDiffs
                : [],
            ratioTypes: next.ratioTypes,
        }),
    };
};
